<!--<style scoped lang="scss">-->
<!--@import './DropdownCurrencyStyle.scss';-->
<!--</style>-->

<template>
  <div :class="[className, theme ? 'theme-long' : '']" v-ClickOutside="closeCurrenciesDropdown">
    <button
      type="button"
      ref="triggerEl"
      :class="[`${className}&#45;&#45;button`, classButton, { active: showCurrenciesDropdown }, {has_border: showBorder}]"
      title="Select currency"
      aria-label="Select currency"
      aria-haspopup="listbox"
      :aria-expanded="showCurrenciesDropdown ? 'true' : 'false'"
      @click="openCurrenciesDropdown"
      @keydown.down.prevent="onTriggerArrow"
      @keydown.up.prevent="onTriggerArrow"
    >
      <span :class="[theme ? 'fs-16 lh-14 pr-10' : 'fs-14 lh-20']">
        <span :class="classButtonText ? classButtonText : [theme ? 'fw-700 black dib w-45 tl fs-16 lh-24 pr-10' : 'fw-500 black']">{{ showCodeCurrent ? selectedCurrency.code : selectedCurrency.symbol}}</span>
        <span v-if='theme' class="currency-long-label fw-500 fs-14 lh-20">{{ selectedCurrency.label }}</span>
      </span>
      <img class='icon' :src="`${ASSETS_CDN}/vectors/general/icon-chevron-down-v2.svg`" alt='chevron down'/>
    </button>
    <div
      :class="[`${className}--dropdown`, { active: showCurrenciesDropdown }]"
      role="listbox"
      aria-label="Select currency"
      @keydown="onMenuKeydown"
    >
      <ul>
        <li v-for="(item, index) in STATIC_CURRENCIES" :key="`header-currencies-${index}`">
          <button
            type="button"
            role="option"
            :ref="el => setRowRef(el, index)"
            :aria-selected="isSelected(item) ? 'true' : 'false'"
            :tabindex="rowTabindex(index)"
            :title="`Select currency: ${item.label}`"
            :aria-label="`Select currency: ${item.label}`"
            @click="changeCurrency(item)"
          >
            <span :class="[theme ? 'dib w-45 tl fs-16 lh-24 fw-700 pr-10' : '']">{{ showCodeCurrent ? item.code : item.symbol }}</span>
            <span :class="[theme ? 'fw-500 fs-14 lh-20' : '']">{{ theme ? ` ${item.label}` : ` — ${item.label}` }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import ClickOutside from '~/directives/ClickOutside'
import { defineComponent, ref, computed, nextTick } from 'vue'
import { useGetCookie } from '~/composables/common/useCustomCookie'
import currenciesApi from '~/composables/api/currenciesApi'
import { useRoute } from 'vue-router'
import { STATIC_CURRENCIES } from '~/enum/currency.enum'
import { debounceHeapTracking } from '~/mixins/heap'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import {assetCDN} from '~/helper/index.js'
import {useCurrenciesStore} from '~/store/pinia/currencies'

export default defineComponent({
  directives: {
    ClickOutside,
  },
  props: {
    theme: {type: String, default: ''},
    classButton: {type: String, default: ''},
    classButtonText: {type: String, default: ''},
    showCodeCurrent: {type: Boolean, default: true},
    showDropdownRight: {type: Boolean, default: true},
    showBorder: {type: Boolean, default: false}
  },
  setup(props) {
    const route = useRoute()
    const showCurrenciesDropdown = ref(false)

    // --- Dropdown-menu ARIA / roving keyboard focus (V15) ---
    const triggerEl = ref(null)
    const rowEls = ref([])
    const activeIndex = ref(0)
    const setRowRef = (el, index) => { if (el) rowEls.value[index] = el }
    const isSelected = (item) => item.code === selectedCurrency.value.code
    const rowTabindex = (index) => (showCurrenciesDropdown.value && index === activeIndex.value ? 0 : -1)
    const setActive = (i) => {
      const n = STATIC_CURRENCIES.length
      activeIndex.value = ((i % n) + n) % n
      nextTick(() => rowEls.value[activeIndex.value]?.focus())
    }
    const focusInitialRow = () => {
      const selIdx = STATIC_CURRENCIES.findIndex((c) => c.code === selectedCurrency.value.code)
      setActive(selIdx >= 0 ? selIdx : 0)
    }
    const onTriggerArrow = () => {
      if (!showCurrenciesDropdown.value) openCurrenciesDropdown()
      if (showCurrenciesDropdown.value) focusInitialRow()
    }
    const onMenuKeydown = (e) => {
      const n = STATIC_CURRENCIES.length
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setActive(activeIndex.value + 1); break
        case 'ArrowUp': e.preventDefault(); setActive(activeIndex.value - 1); break
        case 'Home': e.preventDefault(); setActive(0); break
        case 'End': e.preventDefault(); setActive(n - 1); break
        case 'Escape': e.preventDefault(); closeCurrenciesDropdown(); triggerEl.value?.focus(); break
        default: break
        // Enter / Space activate the focused <button> natively → changeCurrency
      }
    }
    // const currencyDropdownClick = ref(store.state.currenciesStore.currencyChangeByUser)
    const currencyLoaded = ref(false)
    const ASSETS_CDN = assetCDN()
    const currenciesStore = useCurrenciesStore()
    const currencyDropdownClick = ref(currenciesStore.currencyChangeByUser)

    const selectedCurrency = computed(() => {
      // return store.getters['currenciesStore/getCurrencySelected']
      return currenciesStore.getCurrencySelected
    })
    const className = computed(() => {
      return props.showDropdownRight ? 'right-currencies' : 'left-currencies'
    })

    // const currenciesList = computed(() => store.state.currenciesStore.currenciesData)
    const currenciesList = computed(() => currenciesStore.currenciesData)

    const changeCurrency = (item) => {
      const dj_curr = useGetCookie('dj_curr')
      const dj_curr_symbol = useGetCookie('dj_curr_symbol')
      currencyDropdownClick.value += 1
      debounceHeapTracking("Switch Currency", {
        current_currency: selectedCurrency.value.code,
        new_currency: item.code
      })
      debounceAmplitudeScriptTracking("Switch Currency", {
        current_currency: selectedCurrency.value.code,
        new_currency: item.code
      })
      closeCurrenciesDropdown()
      // setCookie('dj_curr', item.code, COOKIE.DURATION)
      // setCookie('dj_curr_symbol', item.symbol, COOKIE.DURATION)
      dj_curr.value = item.code
      dj_curr_symbol.value = item.symbol

      // console.log('changeCurrency',selectedCurrency.value.code,' == ',item)
      const selected = currenciesList.value.find(
        ({ code }) => code === item.code,
      )

      currenciesStore.setCurrencySelected(selected)
      currenciesStore.setCurrencyDropdownClick(currencyDropdownClick.value)

      currenciesApi.postCurrency(selected.code)
    }

    // const tripBooked = computed(() => store.state.currenciesStore.tripBooked)
    const tripBooked = computed(() => currenciesStore.tripBooked)
    const openCurrenciesDropdown = () => {
      if(route.name === 'trip-plan-bookable' || route.name === 'checkout-balance' || route.name === 'trip-preview') {
        if(tripBooked.value) showCurrenciesDropdown.value = false
        else {
          showCurrenciesDropdown.value = !showCurrenciesDropdown.value
        }
      } else {
        showCurrenciesDropdown.value = !showCurrenciesDropdown.value
      }
    }

    const closeCurrenciesDropdown = () => {
      showCurrenciesDropdown.value = false
    }

    return {
      currenciesList,
      changeCurrency,
      selectedCurrency,
      showCurrenciesDropdown,
      closeCurrenciesDropdown,
      openCurrenciesDropdown,
      STATIC_CURRENCIES,
      currencyLoaded,
      className,
      ASSETS_CDN,
      triggerEl,
      setRowRef,
      isSelected,
      rowTabindex,
      onTriggerArrow,
      onMenuKeydown
    }
  }
})
</script>
<style scoped lang='scss'>
  @import './DropdownCurrencyStyle.scss';

  .currency-long-label {
    color: $ink; // §7 ink label (was inline #2D3748); theme-long variant
  }

  // W7-gate — the theme-long currency dropdown sits on the LIGHT-KEPT checkout/enquiry/price-box
  // surface (see HeaderLayout #topInfo note), so its text must STAY dark in dark mode. The label
  // above already pins $ink; the CODE span carries the .black utility which the global dark-flip
  // lifts to near-white → invisible here. Re-pin it dark to match the label (dark-only override).
  [data-theme='dark'] .fs-16.lh-14 .black { color: $ink; }

  .has_border {
   border: 1px solid $brand-green; // §17 (= brandColor(brand,950))
   border-radius: $radius-pill;
   padding: 4px 8px;
  }
</style>
